import { useCurrentDelay } from "@/shared/components/StatusBar";
import { usePassingTheBall } from "@/shared/components/StatusBar/hooks/usePassingTheBall";
import { routeDelaysManager } from "@/setup/routes/delay.routes";
import { routeMillSetup } from "@/setup/routes/production.routes";
import { useUTLSettingsContext } from "@dexteel/mesf-core";
import { Box } from "@mui/material";
import { get } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { NavbarStatusItem } from "./NavbarStatusItem";

/**
 * Status bar component displayed on the right side of the navbar.
 * Shows real-time production metrics and delay information.
 *
 * Features:
 * - Auto-refreshes data every 5 seconds
 * - Clicking navigates to Stops Manager
 * - Auto-navigates when "Passing the Ball" conditions are met
 * - Re-opens popup after configurable interval if user navigates away
 * - Shows pulsing warning when delay is undeclared
 */
// Default reopen interval in seconds
const DEFAULT_REOPEN_SECONDS = 60;

export const NavbarStatusBar = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();

	// Get reopen interval from settings
	const {
		state: { settings },
	} = useUTLSettingsContext();

	const reopenSeconds = useMemo(() => {
		const value = get(
			settings,
			"DELAY_POPUP_REOPEN_SECONDS",
			DEFAULT_REOPEN_SECONDS,
		);
		const parsed = Number.parseInt(String(value), 10);
		return Number.isNaN(parsed) || parsed <= 0
			? DEFAULT_REOPEN_SECONDS
			: parsed;
	}, [settings]);

	// Fetch current delay data with 5-second refresh interval
	const delayData = useCurrentDelay({ refreshInterval: 5000 });
	const {
		delayId,
		delayActive,
		delayDeclared,
		currentDelayTimeString,
		shiftDelayTimeString,
		lastBilletGap,
		finishLineSpeed,
		barsInChainsLabel,
	} = delayData;

	// Passing the Ball logic - determines if user should be prompted
	const { shouldShowPopup, popupType, branchCodeId } = usePassingTheBall({
		delayData,
	});

	// Check if we're on the delays manager page and if modal is open (action=justify in URL)
	const isOnDelaysManager = location.pathname.startsWith(routeDelaysManager);
	const isModalOpen = searchParams.get("action") === "justify";

	// Track which delay we've already navigated to (using state to trigger re-renders)
	const [lastNavigatedDelay, setLastNavigatedDelay] = useState<{
		delayId: number;
		popupType: string;
	} | null>(null);

	// Build the navigation URL
	const buildNavigationUrl = useCallback(
		(
			currentDelayId: number,
			currentPopupType: string,
			currentBranchCodeId: number | null,
		) => {
			let url = `${routeDelaysManager}?action=justify&delayId=${currentDelayId}`;
			if (currentPopupType === "undeclared") {
				url += "&passingMode=operator";
			} else if (currentPopupType === "branch" && currentBranchCodeId) {
				url += `&passingMode=specialist&branchCodeId=${currentBranchCodeId}`;
			}
			return url;
		},
		[],
	);

	// Track the timeout for re-opening
	const reopenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Clear any pending reopen timeout
	const clearReopenTimeout = useCallback(() => {
		if (reopenTimeoutRef.current) {
			clearTimeout(reopenTimeoutRef.current);
			reopenTimeoutRef.current = null;
		}
	}, []);

	// Schedule a re-open after the configured delay
	const scheduleReopen = useCallback(() => {
		clearReopenTimeout();
		reopenTimeoutRef.current = setTimeout(() => {
			// Clear the last navigation state - this triggers a re-render and re-navigation
			setLastNavigatedDelay(null);
		}, reopenSeconds * 1000);
	}, [reopenSeconds, clearReopenTimeout]);

	// Auto-navigate when conditions are met (first time only, don't schedule reopen yet)
	useEffect(() => {
		if (!shouldShowPopup || !delayId || !popupType) {
			clearReopenTimeout();
			return;
		}

		// Don't navigate away from Mill Setup — it has its own delay popup with billets context
		const isOnMillSetup = location.pathname.startsWith(routeMillSetup);
		if (isOnMillSetup) {
			return;
		}

		// Check if we already navigated for this delay/popupType
		const alreadyNavigated =
			lastNavigatedDelay &&
			lastNavigatedDelay.delayId === delayId &&
			lastNavigatedDelay.popupType === popupType;

		if (alreadyNavigated) {
			// Already navigated, don't navigate again (timer handles re-open)
			return;
		}

		// First time for this delay (or after reopen timeout cleared state) - navigate
		setLastNavigatedDelay({ delayId, popupType });

		const url = buildNavigationUrl(delayId, popupType, branchCodeId);
		navigate(url);

		// DON'T schedule reopen here - wait until user closes modal or leaves page
	}, [
		shouldShowPopup,
		delayId,
		popupType,
		branchCodeId,
		navigate,
		buildNavigationUrl,
		clearReopenTimeout,
		lastNavigatedDelay,
	]);

	// Start reopen timer when:
	// 1. User closes the modal (isOnDelaysManager=true but isModalOpen=false), OR
	// 2. User leaves the delays manager page entirely
	useEffect(() => {
		if (!shouldShowPopup || !delayId || !popupType) {
			return;
		}

		const alreadyNavigated =
			lastNavigatedDelay &&
			lastNavigatedDelay.delayId === delayId &&
			lastNavigatedDelay.popupType === popupType;

		if (!alreadyNavigated) {
			return;
		}

		// Modal is open - cancel any timer, user is working
		if (isModalOpen) {
			clearReopenTimeout();
			return;
		}

		// Modal is closed (either on page without action=justify, or left page entirely)
		// Start the timer if not already running
		if (!reopenTimeoutRef.current) {
			scheduleReopen();
		}
	}, [
		shouldShowPopup,
		delayId,
		popupType,
		lastNavigatedDelay,
		isOnDelaysManager,
		isModalOpen,
		scheduleReopen,
		clearReopenTimeout,
	]);

	// Reset navigation tracker when delay ends
	useEffect(() => {
		if (!delayActive) {
			setLastNavigatedDelay(null);
			clearReopenTimeout();
		}
	}, [delayActive, clearReopenTimeout]);

	const handleClick = () => {
		if (delayActive) {
			navigate(`${routeDelaysManager}?action=justify&delayId=${delayId}`);
		} else {
			navigate(routeDelaysManager);
		}
	};

	// Show warning when: (1) delay is undeclared OR (2) passing the ball popup should show
	const showWarning = (delayActive && !delayDeclared) || shouldShowPopup;

	return (
		<Box
			onClick={handleClick}
			sx={{
				display: "flex",
				alignItems: "center",
				ml: "auto",
				px: 1,
				py: 0.5,
				borderRadius: 1,
				cursor: "pointer",
				"&:hover": {
					backgroundColor: "rgba(255,255,255,0.1)",
				},
			}}
		>
			<NavbarStatusItem
				label="Last Gap"
				value={lastBilletGap?.toFixed(2) ?? "-"}
			/>
			<NavbarStatusItem label="Speed" value={finishLineSpeed ?? "-"} />
			<NavbarStatusItem
				label="Shift Delays"
				value={shiftDelayTimeString}
				highlight={delayActive}
			/>
			<NavbarStatusItem label="In Chains" value={barsInChainsLabel} />
			{delayActive && (
				<NavbarStatusItem
					label={popupType === "branch" ? "Needs Detail" : "Current Delay"}
					value={currentDelayTimeString}
					highlight
					warning={showWarning}
				/>
			)}
		</Box>
	);
};
